/**
 * Dashboard.jsx — HealHub Main Dashboard
 *
 * What's here:
 *  - Mood slider + quick thought (saves to Supabase — unchanged)
 *  - Self-care streak (localStorage — unchanged)
 *  - Ambient sounds (unchanged)
 *  - QuickNote card (new — Groq AI, 30/month free)
 *  - Diary card (uses DiaryEntry component — 1 AI insight/day free)
 *  - Usage limit indicators for free users
 *
 * NOT touched:
 *  - Wellness score logic (WellnessModal)
 *  - Supabase mood log
 *  - Streak logic
 *  - Sound player
 *
 * ─── RAZORPAY ─────────────────────────────────────────────────────────────
 * When adding Razorpay, add an "Upgrade" CTA button in the usage banner
 * and wire it to your /api/payment/create-order endpoint.
 * See server.js RAZORPAY section for full frontend code snippet.
 * ─────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link }                   from 'react-router-dom';
import { auth, db }                            from '../firebase';
import { signOut }                             from 'firebase/auth';
import { getDoc, doc }                         from 'firebase/firestore';
import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from 'react-icons/md';
import { FaUserCircle }                        from 'react-icons/fa';
import Diary                                   from '../components/Diary';         // existing component — unchanged
import QuickNoteCard                           from '../components/QuickNote';     // new
import { supabase }                            from '../supabase';
import { authFetch, getUserClaims }            from '../utils/authFetch';
import usePushNotifications                      from '../hooks/usePushNotifications';
import TopStories from '../components/TopStories';
import '../styles/global.css';

const LS_MOOD = 'hh_mood';

// ─── Limit badge shown in top-right corner of each AI card ───────────────
const UsagePill = ({ used, limit, label, isPaid, onUpgrade }) => {
  if (isPaid) return null;
  const pct  = limit ? Math.min(100, (used / limit) * 100) : 0;
  const gone = used >= limit;
  return (
    <div style={{
      display   : 'flex', alignItems: 'center', gap: '8px',
      padding   : '5px 10px', borderRadius: '20px',
      background: gone ? '#fff3e0' : '#f1f8e9',
      border    : `1px solid ${gone ? '#ffb74d' : '#aed581'}`,
      fontSize  : '0.78rem', color: gone ? '#e65100' : '#558b2f',
    }}>
      <div style={{
        width: '40px', height: '5px', borderRadius: '3px',
        background: '#e0e0e0', overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: '3px',
          background: gone ? '#ef5350' : '#8bc34a',
          transition: 'width 0.3s',
        }} />
      </div>
      {gone
        ? <span>Limit reached — <button onClick={onUpgrade} style={{ background: 'none', border: 'none', color: '#e65100', fontWeight: 700, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Upgrade</button></span>
        : <span>{used}/{limit} {label}</span>
      }
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();

  // ─── Theme ──────────────────────────────────────────────────────────────
  const [theme,          setTheme]          = useState('light-theme');
  const [showInsightMenu, setShowInsightMenu] = useState(false);
  const hideTimeout = useRef(null);

  // Register push notifications silently — FCM is free, no limits
  // Prompts user for permission once, then registers token with backend
  // Notifications fire for: report ready, subscription granted, streaks (future)
  usePushNotifications();

  // ─── Streak (unchanged logic) ────────────────────────────────────────────
  const [streakCount,   setStreakCount]   = useState(0);
  const [streakMessage, setStreakMessage] = useState('Start your streak by completing a task!');

  // ─── Ambient sounds (unchanged) ─────────────────────────────────────────
  const [currentSound, setCurrentSound] = useState(null);
  const [audioUrl,     setAudioUrl]     = useState('');
  const soundSources = { rain: '/rain.mp3', forest: '/forest.mp3', ocean: '/ocean.mp3' };

  // ─── Mood (unchanged) ────────────────────────────────────────────────────
  const [moodScore,    setMoodScore]    = useState(50);
  const [shortThought, setShortThought] = useState('');
  const [moodFeedback, setMoodFeedback] = useState('');

  // ─── Diary AI (passed to existing Diary component) ──────────────────────
  const [aiResponse,   setAIResponse]   = useState('');
  const [isAnalyzing,  setIsAnalyzing]  = useState(false);

  // ─── Usage tracking ──────────────────────────────────────────────────────
  const [isPaid,   setIsPaid]   = useState(false);
  const [usage,    setUsage]    = useState(null); // from /api/usage
  const [usagePending, setUsagePending] = useState(true);

  // ─── Diary limit gate (local state for quick feedback) ──────────────────
  const [diaryLimitReached, setDiaryLimitReached] = useState(false);

  // ─── Init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const savedTheme  = localStorage.getItem('theme') || 'light-theme';
    setTheme(savedTheme);
    document.body.className = savedTheme;

    const savedStreak = parseInt(localStorage.getItem('streakCount')) || 0;
    setStreakCount(savedStreak);

    const savedMood = parseInt(localStorage.getItem(LS_MOOD));
    if (!isNaN(savedMood)) setMoodScore(savedMood);

    // Profile completion check (unchanged)
    const checkProfile = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, 'users', user.uid));
      if (!snap.exists() || snap.data().profileCompleted !== true) {
        navigate('/setup-profile'); return;
      }
      // After profile is set up, send new users through onboarding
      if (snap.data().onboardingDone !== true) {
        navigate('/onboarding'); return;
      }
    };
    checkProfile();

    // Load user tier + usage stats
    const loadUsage = async () => {
      try {
        const claims = await getUserClaims();
        setIsPaid(claims.paid);
        const res  = await authFetch('/api/usage');
        const data = await res.json();
        setUsage(data);
        if (!claims.paid && data.diaryInsight?.used >= data.diaryInsight?.limit) {
          setDiaryLimitReached(true);
        }
      } catch { /* silent */ }
      finally { setUsagePending(false); }
    };
    if (auth.currentUser) loadUsage();
  }, []);

  // ─── Navbar handlers ─────────────────────────────────────────────────────
  const handleInsightMouseEnter = () => { clearTimeout(hideTimeout.current); setShowInsightMenu(true); };
  const handleInsightMouseLeave = () => { hideTimeout.current = setTimeout(() => setShowInsightMenu(false), 150); };

  const toggleTheme = () => {
    const themes = ['light-theme', 'dark-theme', 'zen-theme'];
    const next   = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(next); localStorage.setItem('theme', next); document.body.className = next;
  };

  const handleLogout = async () => { try { await signOut(auth); navigate('/'); } catch { alert('Failed to log out'); } };

  // ─── Streak (unchanged logic) ────────────────────────────────────────────
  const updateStreak = () => {
    const today     = new Date().toDateString();
    const lastCheck = localStorage.getItem('lastCheckInDate');
    let   streak    = parseInt(localStorage.getItem('streakCount')) || 0;
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);

    if (lastCheck === today)                        { setStreakMessage('You already checked in today!'); }
    else if (lastCheck === yesterday.toDateString()) { streak++; setStreakMessage("You're on a roll! Keep going!"); }
    else                                             { streak = 1; setStreakMessage('Streak started! Great job!'); }

    localStorage.setItem('streakCount', streak);
    localStorage.setItem('lastCheckInDate', today);
    setStreakCount(streak);
  };

  // ─── Ambient sounds (unchanged) ─────────────────────────────────────────
  const toggleSound = (type) => {
    if (currentSound === type) { setCurrentSound(null); setAudioUrl(''); }
    else { setCurrentSound(type); setAudioUrl(soundSources[type]); }
  };

  // ─── Mood (unchanged logic) ──────────────────────────────────────────────
  const handleMoodChange  = (v) => { setMoodScore(v); localStorage.setItem(LS_MOOD, v); };
  const handleMoodSubmit  = async () => {
    let feedback = moodScore <= 33
      ? 'We notice you are feeling low 😞. Check out Resources for books, music, and stories to lift your mood!'
      : moodScore <= 66
      ? 'Feeling okay 😐? Reflect on what went right and what could be improved. Resources can help!'
      : 'You are feeling great 😊! Keep up the good vibes!';
    setMoodFeedback(feedback);

    const user = auth.currentUser;
    if (!user) return;
    const { error: insertError } = await supabase.from('mood_logs').insert([{
      user_id: user.uid, score: moodScore, short_thought: shortThought,
      created_at: new Date().toISOString(),
    }]);
    if (insertError) { console.error('Failed to save mood log:', insertError); return; }

    // Prune to last 50 logs (unchanged)
    const { count, error: countError } = await supabase.from('mood_logs')
      .select('id', { count: 'exact', head: true }).eq('user_id', user.uid);
    if (countError) return;
    if (count > 50) {
      const excess = count - 50;
      const { data: oldest } = await supabase.from('mood_logs').select('id')
        .eq('user_id', user.uid).order('created_at', { ascending: true }).limit(excess);
      if (oldest?.length) {
        await supabase.from('mood_logs').delete().in('id', oldest.map(l => l.id));
      }
    }
  };

  /**
   * handleAIAnalysis — passed to existing <Diary> component.
   * Now uses authFetch (adds Firebase token) and enforces daily limit.
   * The Diary component calls this when user clicks "Get AI Insight".
   */
  const handleAIAnalysis = async (diaryEntry, title = '') => {
    if (diaryLimitReached && !isPaid) {
      setAIResponse('⚠️ You\'ve used your 1 free diary insight for today. Your entry was still saved. Upgrade to ₹35/month for unlimited insights. 💙');
      return;
    }
    setIsAnalyzing(true);
    setAIResponse('');
    try {
      const res  = await authFetch('/api/diary/analyze', {
        method: 'POST',
        body  : JSON.stringify({ text: diaryEntry, title }),
      });
      const data = await res.json();

      if (res.status === 429) {
        setDiaryLimitReached(true);
        setAIResponse(data.error || '⚠️ Daily diary insight limit reached. Come back tomorrow!');
        // Refresh usage
        const u = await authFetch('/api/usage').then(r => r.json());
        setUsage(u);
        return;
      }
      setAIResponse(data.insight || 'No insight generated.');
      // Refresh usage count
      const u = await authFetch('/api/usage').then(r => r.json());
      setUsage(u);
    } catch {
      setAIResponse('AI analysis failed. Please try again later.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Upgrade handler — TODO: wire to Razorpay when integrating payments
  // RAZORPAY: Replace this navigation with your Razorpay checkout flow
  const handleUpgrade = () => navigate('/pricing');

  return (
    <div className="dashboard-container">

      {/* ─── NAVBAR ──────────────────────────────────────────────────────── */}
      <nav className="navbar">
        <div className="navbar-left">
          <a href="/">Home</a>
          <a href="/resources">Resources</a>
          <a href="/community">Mentors</a>
          <Link to="/feed" className="nav-item">Feed</Link>
          <a href="/chatbot">Chat with Saksham</a>
          <div
            className="nav-dropdown"
            onMouseEnter={handleInsightMouseEnter}
            onMouseLeave={handleInsightMouseLeave}
          >
            <span className="nav-item">Insight ▾</span>
            {showInsightMenu && (
              <div className="dropdown-menu" onMouseEnter={handleInsightMouseEnter} onMouseLeave={handleInsightMouseLeave}>
                <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                <Link to="/insight" className="dropdown-link">Look at Report</Link>
              </div>
            )}
          </div>
          <Link to="/chattr" className="nav-item">Chattr</Link>
          <Link to="/cluecue" className="nav-item">ClueCue 🎭</Link>
        </div>
        <div className="navbar-center">
          <span className="healhub-brand"><span className="hug-icon">💖🌿</span> HealHub</span>
          <Link to="/about" className="nav-item">About Us</Link>
        </div>
        <div className="navbar-right">
          <button onClick={toggleTheme} className="theme-switcher-btn">
            {theme === 'light-theme' ? <MdOutlineLightMode /> : theme === 'dark-theme' ? <MdModeNight /> : <MdFilterVintage />}
          </button>
          <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24} /></Link>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </nav>

      {/* ─── UPGRADE BANNER (free users only) ──────────────────────────── */}
      {!isPaid && !usagePending && (
        <div style={{
          background: 'linear-gradient(90deg, #e8f5e9, #e3f2fd)',
          borderBottom: '1px solid #c8e6c9',
          padding: '10px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '8px', fontSize: '0.85rem',
        }}>
          <span style={{ color: '#2e7d32' }}>
            💙 Free plan — diary insights left today: <strong>{Math.max(0, (usage?.diaryInsight?.limit || 1) - (usage?.diaryInsight?.used || 0))}</strong> &nbsp;|&nbsp;
            Saksham messages left: <strong>{Math.max(0, (usage?.chat?.limit || 5) - (usage?.chat?.used || 0))}</strong>
          </span>
          <button
            onClick={handleUpgrade}
            style={{
              padding: '6px 16px', background: '#2e7d32', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: '700',
            }}
          >
            Upgrade to Plus — ₹35/month
          </button>
        </div>
      )}

      <div className="dashboard-grid">

        {/* ─── QUICK NOTE CARD (new) ────────────────────────────────────── */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span />
            {usage && (
              <UsagePill
                used={usage.quickNote?.used || 0}
                limit={usage.quickNote?.limit}
                label="notes/month"
                isPaid={isPaid}
                onUpgrade={handleUpgrade}
              />
            )}
          </div>
          {/* QuickNote handles its own auth + API calls */}
          <QuickNoteCard
            onNoteAdded={() => {
              // Refresh usage after a note is added
              authFetch('/api/usage').then(r => r.json()).then(setUsage).catch(() => {});
            }}
          />
        </div>
        {/* ─── TOP STORIES CARD (new — opposite of QuickNote) ─────────── */}
        <div className="card">
          <TopStories />
        </div>

        {/* ─── DIARY ENTRY CARD (existing Diary component — unchanged) ──── */}
        <div className="card diary-entry-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <h3 style={{ margin: 0 }}>📓 Diary</h3>
            {usage && (
              <UsagePill
                used={usage.diaryInsight?.used || 0}
                limit={usage.diaryInsight?.limit}
                label="insights/day"
                isPaid={isPaid}
                onUpgrade={handleUpgrade}
              />
            )}
          </div>
          {/* Diary component is unchanged — we pass our updated AI handler */}
          <Diary
            onAIAnalyze={handleAIAnalysis}
            aiResponse={aiResponse}
            isAnalyzing={isAnalyzing}
          />
          {/* Show limit warning inside diary card if hit */}
          {diaryLimitReached && !isPaid && (
            <div style={{
              marginTop: '10px', padding: '10px 14px',
              background: '#fff3e0', borderRadius: '10px',
              fontSize: '0.85rem', color: '#e65100',
            }}>
              ⚠️ You've used today's free diary insight. Your entries are still saved.{' '}
              <button onClick={handleUpgrade} style={{ background: 'none', border: 'none', color: '#e65100', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                Upgrade for unlimited insights →
              </button>
            </div>
          )}
        </div>

        {/* ─── MOOD ANALYSIS CARD (unchanged) ──────────────────────────── */}
        <div className="card mood-analysis-card">
          <h3>How was your day?</h3>
          <p className="mood-question-label">Drag the slider to reflect your mood.</p>
          <div className="mood-slider-container">
            <input
              type="range" min="0" max="100" value={moodScore}
              onChange={(e) => handleMoodChange(Number(e.target.value))}
              style={{ width: '100%', marginTop: '10px' }}
            />
            <div className="mood-labels"><span>😞</span><span>😐</span><span>😊</span></div>
          </div>
          <input
            type="text" className="short-thought-input"
            placeholder="Share your thoughts in 10 words"
            value={shortThought}
            onChange={(e) => setShortThought(e.target.value)}
            style={{ width: '100%', marginTop: '10px', padding: '5px' }}
          />
          <button style={{ marginTop: '10px', padding: '8px 12px', cursor: 'pointer' }} onClick={handleMoodSubmit}>
            Submit
          </button>
          {moodFeedback && <p style={{ marginTop: '10px', fontWeight: 'bold' }}>{moodFeedback}</p>}
        </div>

        {/* ─── STREAK CARD (unchanged) ─────────────────────────────────── */}
        <div className="card streak-card">
          <h3>Self-Care Streak 🔥</h3>
          <div className="streak-display">
            <span id="streak-count">{streakCount}</span>
            <p>days in a row!</p>
          </div>
          <p className="streak-message">{streakMessage}</p>
          <button
            onClick={updateStreak}
            style={{ marginTop: '10px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer' }}
          >
            ✅ Check-in Today
          </button>
        </div>

        {/* ─── AMBIENT SOUNDS (unchanged) ──────────────────────────────── */}
        <div className="card sound-player-card">
          <h3>Ambient Sounds 🎧</h3>
          <div className="sound-buttons">
            <button className={`sound-btn ${currentSound === 'rain'   ? 'active' : ''}`} onClick={() => toggleSound('rain')}>Rain</button>
            <button className={`sound-btn ${currentSound === 'forest' ? 'active' : ''}`} onClick={() => toggleSound('forest')}>Forest</button>
            <button className={`sound-btn ${currentSound === 'ocean'  ? 'active' : ''}`} onClick={() => toggleSound('ocean')}>Ocean</button>
          </div>
          {audioUrl && <audio src={audioUrl} controls autoPlay style={{ marginTop: '16px', width: '100%' }} />}
        </div>

      </div>
    </div>
  );
};

export default Dashboard;